import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
  AppBar,
  Toolbar,
  Typography,
  Button
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Download as ExportIcon
} from '@mui/icons-material';
import MultiPhaseChatPanel from './AIFlowBuilder/MultiPhaseChatPanel';
import ModelSelector from './AIFlowBuilder/ModelSelector';
import api from '../api/axios';

/**
 * AI Flow Builder Modal
 * Main container for the AI-assisted flow building interface
 */
const AIFlowBuilderModal = ({ open, onClose, projectId, currentFlow, onFlowUpdate }) => {
  const [sessions, setSessions] = useState([]);
  const [activeSessionIndex, setActiveSessionIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);

  // Load available models on mount
  useEffect(() => {
    if (open) {
      loadAvailableModels();
    }
  }, [open]);

  // Load existing sessions for this project
  useEffect(() => {
    if (open && projectId && availableModels.length > 0) {
      loadSessions();
    }
  }, [open, projectId, availableModels]);

  const loadAvailableModels = async () => {
    try {
      // Pass projectId to check project-specific API keys
      const response = await api.get(`/ai-flow-builder/available-models?projectId=${projectId}`);
      setAvailableModels(response.data.models || []);
    } catch (error) {
      console.error('Error loading available models:', error);
      // Fallback to OpenAI if available
      setAvailableModels([
        { id: 'gpt-4o', name: 'GPT-4o', provider: 'OpenAI' }
      ]);
    }
  };

  const loadSessions = async () => {
    try {
      const response = await api.get(`/ai-flow-builder/sessions/${projectId}`);
      if (response.data.sessions && response.data.sessions.length > 0) {
        setSessions(response.data.sessions);
      } else {
        // Create a new session if none exist
        await createNewSession();
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
      // Create a new session on error
      await createNewSession();
    }
  };

  const createNewSession = async (model) => {
    try {
      setLoading(true);
      
      // Use provided model or first available model
      const selectedModel = model || (availableModels.length > 0 ? availableModels[0].id : 'gpt-4o');
      
      const response = await api.post('/ai-flow-builder/session', {
        projectId,
        flowId: currentFlow?.id || null,
        selectedModel
      });

      const newSession = response.data.session;
      setSessions([...sessions, newSession]);
      setActiveSessionIndex(sessions.length);
    } catch (error) {
      console.error('Error creating session:', error);
    } finally {
      setLoading(false);
    }
  };

  const closeSession = async (index) => {
    if (sessions.length === 1) {
      // Don't close the last session, just close the modal
      handleClose();
      return;
    }

    try {
      const session = sessions[index];
      await api.delete(`/ai-flow-builder/session/${session.sessionId}`);
      
      const newSessions = sessions.filter((_, i) => i !== index);
      setSessions(newSessions);
      
      if (activeSessionIndex >= index && activeSessionIndex > 0) {
        setActiveSessionIndex(activeSessionIndex - 1);
      }
    } catch (error) {
      console.error('Error closing session:', error);
    }
  };

  const exportConversation = async () => {
    const session = sessions[activeSessionIndex];
    if (!session) return;

    try {
      const response = await api.get(`/ai-flow-builder/session/${session.sessionId}`);
      const sessionData = response.data.session;

      // Create a downloadable transcript
      let transcript = `AI Flow Builder Conversation Export\n`;
      transcript += `Session: ${sessionData.sessionName}\n`;
      transcript += `Date: ${new Date(sessionData.createdAt).toLocaleString()}\n`;
      transcript += `Model: ${sessionData.selectedModel}\n`;
      transcript += `\n${'='.repeat(80)}\n\n`;

      for (const message of sessionData.messages) {
        const timestamp = new Date(message.timestamp).toLocaleTimeString();
        transcript += `[${timestamp}] ${message.role.toUpperCase()}:\n`;
        transcript += `${message.content}\n\n`;
      }

      const blob = new Blob([transcript], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `flow-builder-conversation-${sessionData.sessionId}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting conversation:', error);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleFlowGenerated = (flowData) => {
    // Update the session with new flow state
    const updatedSessions = [...sessions];
    updatedSessions[activeSessionIndex].currentFlowState = flowData;
    setSessions(updatedSessions);
    
    // Display in canvas WITHOUT saving to database (preview mode)
    // User can manually save when satisfied
    if (onFlowUpdate && flowData) {
      onFlowUpdate(flowData, true); // Pass true for preview mode (no auto-save)
    }
  };

  const activeSession = sessions[activeSessionIndex];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          m: 0,
          maxHeight: '100vh',
          height: '100%',
          maxWidth: '50vw',
          borderRadius: 0,
          borderLeft: '2px solid #e0e0e0'
        }
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'transparent' // No backdrop so user can see flow canvas
        }
      }}
    >
      <AppBar position="static" color="default" elevation={0} sx={{ borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            AI Flow Builder Assistant
          </Typography>

          {activeSession && (
            <ModelSelector
              sessionId={activeSession.sessionId}
              currentModel={activeSession.selectedModel}
              projectId={projectId}
              onModelChange={(newModel) => {
                const updatedSessions = [...sessions];
                updatedSessions[activeSessionIndex].selectedModel = newModel;
                setSessions(updatedSessions);
              }}
            />
          )}

          <Tooltip title="Export Conversation">
            <IconButton onClick={exportConversation} sx={{ ml: 1 }}>
              <ExportIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Close">
            <IconButton onClick={handleClose} sx={{ ml: 1 }}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>

        {/* Session Tabs */}
        <Box sx={{ borderTop: '1px solid #e0e0e0', display: 'flex', alignItems: 'center' }}>
          <Tabs
            value={activeSessionIndex}
            onChange={(e, newValue) => setActiveSessionIndex(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ flexGrow: 1 }}
          >
            {sessions.map((session, index) => (
              <Tab
                key={session.sessionId}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{session.sessionName || 'New Chat'}</span>
                    <Box
                      component="span"
                      onClick={(e) => {
                        e.stopPropagation();
                        closeSession(index);
                      }}
                      sx={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        p: 0.5,
                        borderRadius: '50%',
                        '&:hover': {
                          bgcolor: 'rgba(0, 0, 0, 0.1)'
                        }
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </Box>
                  </Box>
                }
              />
            ))}
          </Tabs>
          
          <Tooltip title="New Conversation">
            <Button
              startIcon={<AddIcon />}
              onClick={() => createNewSession()}
              disabled={loading}
              sx={{ m: 1 }}
              variant="outlined"
              size="small"
            >
              New
            </Button>
          </Tooltip>
        </Box>
      </AppBar>

      <DialogContent sx={{ p: 0, height: '100%', display: 'flex', overflow: 'hidden' }}>
        {activeSession ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
            {/* Info Banner */}
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="body2" color="text.secondary">
                💡 Flow changes will appear directly in the Visual Flow Editor canvas on the left. You can see updates in real-time as the AI builds your flow.
              </Typography>
            </Box>

            {/* Chat Panel - Full Width */}
            <Box sx={{ flexGrow: 1, height: '100%', overflow: 'hidden' }}>
              <MultiPhaseChatPanel
                session={activeSession}
                currentFlow={currentFlow}
                onFlowGenerated={handleFlowGenerated}
                onSessionNameChange={(newName) => {
                  // Update the session name in local state
                  const updatedSessions = [...sessions];
                  updatedSessions[activeSessionIndex].sessionName = newName;
                  setSessions(updatedSessions);
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <Typography color="text.secondary">Loading session...</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AIFlowBuilderModal;

